import { useState, ChangeEvent, ReactElement, useEffect } from 'react';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { Alert, Box, FormControl, useTheme } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTranslation } from 'react-i18next';
import saveAs from 'file-saver';
import { useSnackbar } from 'notistack';
import Dialog from 'components/molecules/Dialog/Dialog';
import { CheckboxGroupItem } from 'components/molecules/CheckboxGroup/CheckboxGroup';
import { AppBaseButton } from 'components/AppBaseButton/AppBaseButton';
import { ConfirmationDialog } from 'components/ConfirmationDialog/ConfirmationDialog';
import { getAppConfig } from 'config/config';
import { useAppSelector } from 'redux/hooks';
import { prepareAuthHeaders } from 'config/authConfig';
import { Calculation, CalculationType, Project } from '../../../types/domainTypes';
import Checkbox from '../../atoms/Checkbox/Checkbox';
import FormLabel from '@mui/material/FormLabel';

type ReportSelectDialogComponentProps = {
  project: Project;
  open: boolean;
  onClose: () => void;
};

const ReportSelectDialogComponent = ({ project, open, onClose }: ReportSelectDialogComponentProps): ReactElement => {
  const theme = useTheme();
  const discardDialogFullScreen = useMediaQuery(theme.breakpoints.down('xs'));
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [calcsToExport, setCalcsToExport] = useState<string[]>([]);
  const [isDiscardConfirmationOpen, setDiscardConfirmationOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const truncatedCalculationItems = (project.calculations || []).reduce(
    (acc: { name: string; id: string; type: string }[], value: Calculation) => [
      ...acc,
      { name: value.details.name, id: value.id, type: value.details.type },
    ],
    []
  );
  const calculationCheckboxItems = truncatedCalculationItems.reduce(
    (acc: CheckboxGroupItem<string>[], value) => [
      ...acc,
      {
        label: value.name,
        value: value.id,
        helperText:
          value.type === CalculationType.FastenerCheck
            ? `${t('project-details-page.report-export-dialog.diagrams-unavailable')}`
            : `${t('project-details-page.report-export-dialog.diagrams-available')}`,
      },
    ],
    []
  );
  const mixedCalculationTypes = truncatedCalculationItems.some((calc) => calc.type === CalculationType.FastenerCheck);
  const [includeLoadDiagrams, setIncludeLoadDiagrams] = useState(localStorage.getItem('include-load-diagrams') === 'true');
  const [includeCalculationDiagrams, setIncludeCalculationDiagrams] = useState(
    localStorage.getItem('include-calculation-diagrams') === 'true'
  );
  useEffect(() => {
    localStorage.setItem('include-load-diagrams', String(includeLoadDiagrams));
    localStorage.setItem('include-calculation-diagrams', String(includeCalculationDiagrams));
  }, [includeLoadDiagrams, includeCalculationDiagrams]);
  const diagramCheckboxItems: CheckboxGroupItem<string>[] = [
    {
      label: `${t('project-details-page.report-export-dialog.include-diagram-options.load-diagrams')}`,
      value: 'loadDiagrams',
    },
    {
      label: `${t('project-details-page.report-export-dialog.include-diagram-options.calculation-result-diagrams')}`,
      value: 'calculationDiagrams',
    },
  ];
  const division = useAppSelector((state) => state.user.division);

  const handleCalculationChange = (e: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    if (checked) {
      setCalcsToExport([...calcsToExport, e.target.value]);
    } else {
      setCalcsToExport(calcsToExport.filter((x) => x !== e.target.value));
    }
  };

  const handleSelectAllChange = (e: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    if (checked) {
      setCalcsToExport(truncatedCalculationItems.map((c) => c.id));
    } else {
      setCalcsToExport([]);
    }
  };

  const handleDiagramChange = (e: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    if (e.target.value === 'loadDiagrams') {
      setIncludeLoadDiagrams(checked);
    } else if (e.target.value === 'calculationDiagrams') {
      setIncludeCalculationDiagrams(checked);
    }
  };

  const canExport = () => Array.isArray(calcsToExport) && calcsToExport.length > 0;

  const handleClose = () => {
    if (canExport()) {
      setDiscardConfirmationOpen(true);
    } else {
      onClose();
    }
  };

  const handleExport = async () => {
    const requestHeaders: HeadersInit = new Headers();
    requestHeaders.set('Content-Type', 'application/json');

    try {
      setIsDownloading(true);

      const res = await fetch(
        `${getAppConfig().apiUrl}/report/${project.id}?timezone=${
          Intl.DateTimeFormat().resolvedOptions().timeZone
        }&division=${division}&showLoadDiagrams=${includeLoadDiagrams}&showCalculationDiagrams=${includeCalculationDiagrams}`,
        {
          headers: await prepareAuthHeaders(requestHeaders),
          method: 'POST',
          body: JSON.stringify(calcsToExport),
        }
      );

      if (res.ok) {
        const blob = await res.blob();
        saveAs(blob, `${project.name}.pdf`);
        onClose();
      } else {
        enqueueSnackbar(t('calculation-page.results-panel.pdf-generation-error', { status: `${res.status} ${res.statusText}` }), {
          variant: 'error',
        });
      }
    } catch (error) {
      enqueueSnackbar(t('calculation-page.results-panel.pdf-download-error'), {
        variant: 'error',
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const handleConfirmationDialogClose = () => {
    setDiscardConfirmationOpen(false);
    onClose();
  };

  const diagramAvailabilityAlert: JSX.Element = (
    <Alert severity="info" sx={{ marginBottom: '16px' }}>
      <span data-testid="alert-message-title">{t('project-details-page.report-export-dialog.info-message')}</span>
    </Alert>
  );

  return (
    <>
      <Dialog
        title={t('project-details-page.report-export-dialog.title')}
        loading={!truncatedCalculationItems}
        onDialogClose={handleClose}
        open={open}
        PaperProps={{
          sx: {
            minHeight: '90vh',
          },
        }}
        BackdropProps={{ appear: false, invisible: isDiscardConfirmationOpen }}
      >
        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column' }}>
          {mixedCalculationTypes && diagramAvailabilityAlert}
          <FormControl>
            <FormLabel>{t('project-details-page.report-export-dialog.label-select-calculations')}</FormLabel>
            <Checkbox
              key={'item-select-all'}
              checked={calcsToExport.length === truncatedCalculationItems.length}
              indeterminate={calcsToExport.length > 0 && calcsToExport.length < truncatedCalculationItems.length}
              label={t('project-details-page.report-export-dialog.label-all-calculations')}
              value={'selectAll'}
              onChange={handleSelectAllChange}
              disabled={isDownloading}
            />
            <Box sx={{ marginLeft: '5%' }}>
              {calculationCheckboxItems.map((item, index) => (
                <Checkbox
                  key={`calculation-item-${index}`}
                  checked={calcsToExport.includes(item.value)}
                  label={item.label}
                  value={item.value}
                  onChange={handleCalculationChange}
                  disabled={isDownloading}
                  helperText={item.helperText}
                />
              ))}
            </Box>
          </FormControl>
          <FormControl sx={{ marginTop: '20px' }}>
            <FormLabel>{t('project-details-page.report-export-dialog.label-select-include-diagrams')}</FormLabel>
            {diagramCheckboxItems.map((option, index) => (
              <Checkbox
                key={`diagram-option-${index}`}
                label={option.label}
                value={option.value}
                disabled={isDownloading}
                checked={option.value === 'loadDiagrams' ? includeLoadDiagrams : includeCalculationDiagrams}
                onChange={handleDiagramChange}
              />
            ))}
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px' }}>
          <AppBaseButton
            variant="contained"
            onClick={handleExport}
            disabled={!canExport() || isDownloading}
            isLoading={isDownloading}
            sx={{ width: '190px' }}
          >
            {t('project-details-page.report-export-dialog.action-export')}
          </AppBaseButton>
        </DialogActions>
      </Dialog>
      <ConfirmationDialog
        isOpen={isDiscardConfirmationOpen}
        isFullscreen={discardDialogFullScreen}
        handleClose={() => setDiscardConfirmationOpen(false)}
        handleConfirm={handleConfirmationDialogClose}
        title={t('common.edit-dialog.discard-dialog.header')}
        questionText={t('project-details-page.report-export-dialog.discard-dialog.question')}
        cancelText={t('common.edit-dialog.discard-dialog.action-cancel')}
        confirmText={t('common.edit-dialog.discard-dialog.action-confirm')}
      />
    </>
  );
};

export default ReportSelectDialogComponent;
